import { type InputHTMLAttributes, type ReactNode, useId } from "react";
import { SearchIcon } from "./icons";
import { cn, focusRing } from "./utils";

export type SwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
  className?: string;
};

export function Switch({ checked, onChange, label, disabled, className }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative h-7 w-[46px] flex-none cursor-pointer rounded-full transition-colors disabled:opacity-50",
        checked ? "bg-blue" : "bg-line-strong",
        focusRing,
        className,
      )}
    >
      <span
        className={cn(
          "absolute top-[3px] h-[22px] w-[22px] rounded-full bg-linen transition-[left]",
          checked ? "left-[21px]" : "left-[3px]",
        )}
      />
    </button>
  );
}

export type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> & {
  label: string;
  /** Hide the visible label and keep it for screen readers. */
  hideLabel?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
};

export function TextField({ label, hideLabel = false, hint, error, id, className, ...rest }: TextFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={inputId} className={cn("text-[13px] font-semibold text-ink", hideLabel && "sr-only")}>
        {label}
      </label>
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          "h-12 rounded-field border bg-surface px-4 font-sans text-[15px] text-ink placeholder:text-ink-meta",
          "focus:border-blue",
          error ? "border-scarlet" : "border-line",
          focusRing,
        )}
        {...rest}
      />
      {hint && !error && (
        <span id={hintId} className="text-xs text-ink-meta">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="text-[13px] font-semibold text-scarlet">
          {error}
        </span>
      )}
    </div>
  );
}

export type SearchFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "onChange"> & {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Keyboard shortcut shown at the end, such as ⌘K. */
  shortcut?: string;
  size?: "sm" | "md";
};

export function SearchField({
  label,
  value,
  onChange,
  shortcut,
  size = "md",
  className,
  ...rest
}: SearchFieldProps) {
  return (
    <label
      className={cn(
        "flex items-center gap-2.5 bg-surface-header text-ink-secondary",
        "focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-blue",
        size === "md" ? "h-[46px] rounded-field px-4" : "h-[42px] rounded-[10px] pr-2.5 pl-3.5",
        className,
      )}
    >
      <SearchIcon size={size === "md" ? 19 : 17} />
      <input
        type="search"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "min-w-0 flex-1 border-0 bg-transparent font-sans text-ink outline-none placeholder:text-ink-secondary",
          size === "md" ? "text-[15px]" : "text-sm",
        )}
        {...rest}
      />
      {shortcut && (
        <kbd className="rounded-md border border-line bg-linen px-[7px] py-0.5 font-sans text-xs font-semibold text-ink-secondary">
          {shortcut}
        </kbd>
      )}
    </label>
  );
}
